/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState} from 'react'
import {Helmet} from 'react-helmet'
import {Button, ButtonGroup, Popover, OverlayTrigger} from 'react-bootstrap'
import { DomainPageTitle } from '_metronic/layout/core/DomainPageData'
import Searchbar from 'app/components/Searchbar'


const DomainChecks: React.FC = () => {
  const [activeButton, setActiveButton] = useState('ZAP')
  const [searchTerm, setSearchTerm] = useState('')

  const data = {
    ZAP: [
      {
        title: 'Server accepts invalid JWT tokens',
        description:
          "The server verifying the JWT, accepts accepts tokens with an invalid signature. The signature of a JWT is crucial for ensuring its integrity and authenticity; it confirms that the token was issued by a trusted source and has not been tampered with. A failure in this verification process means a bad actor could alter the contents of a JWT (such as the payload data indicating the user's identity or permissions) without the application detecting the modification.",
        severity: 'Critical',
      },
      {
        title: 'Heartbleed OpenSSL Vulnerability',
        description:
          'The TLS and DTLS implementations in OpenSSL 1.0.1 before 1.0.1g do not properly handle Heartbeat Extension packets, which allows remote attackers to obtain sensitive information from process memory via crafted packets that trigger a buffer over-read, potentially disclosing sensitive information.',
        severity: 'Critical',
      },
      {
        title: 'Content Security Policy (CSP) header not set',
        description:
          'Content Security Policy (CSP) is a first line of defense agains common attacks including Cross Site Scripting (XSS) and data injection attacks. These attacks are used for everything from data theft via account takeovers to site defacement or distribution of malware. CSP config allows you to declare what content can be loaded and executed via a standard HTTP header. You can whitelist JavaScript, CSS, HTML frames, fonts, images and embeddable objects such as Java applets, ActiveX, audio and video files.',
        severity: 'Critical',
      },
      {
        title: 'Content Security Policy (CSP) Report-Only header found',
        description:
          'The response contained a Content-Security-Policy-Report-Only header, this may indicate a work-in-progress implementation, or an oversight in promoting pre-Prod to Prod, etc.',
        severity: 'Critical',
      },
      {
        title: 'TLS not enforced with valid HSTS header',
        description:
          "HTTP Strict Transport Security (HSTS) is a mechanism whereby a web server forces web browsers to interact with it using only secure HTTPS connections (i.e. HTTP layered over TLS/SSL). By enforcing this, you are protecting your users from various man-in-the-middle attacks because it's harder for attackers to downgrade connections to HTTP instead of HTTPS.",
        severity: 'Critical',
      },
      {
        title: 'HSTS header is missing',
        description:
          "HTTP Strict Transport Security (HSTS) is a mechanism whereby a web server forces web browsers to interact with it using only secure HTTPS connections (i.e. HTTP layered over TLS/SSL). By enforcing this, you are protecting your users from various man-in-the-middle attacks because it's harder for attackers to downgrade connections to HTTP instead of HTTPS.",
        severity: 'High',
      },
      {
        title: 'Session cookie is not secured',
        description:
          "The cookie holding the session token should have the 'httpOnly' and 'secure' attribute enabled. These attributes are crucial to ensure that this token is secured from 3rd party scripts obtaining them.",
        severity: 'High',
      },
      {
        title: 'CSP configuration is not restrictive enough',
        description:
          "Content Security Policy (CSP) is a first line of defense agains common attacks including Cross Site Scripting (XSS) and data injection attacks. In this case your CSP header is set, but it's not restrictive enough to provide the best defense.",
        severity: 'Medium',
      },
      {
        title: "Authentication cookie can be leaked to any subdomain",
        description:
          'Cookies can be scoped by domain or path. The domain scope applied to a cookie determines which domains can access it. For example, a cookie can be scoped strictly to a subdomain e.g. www.nottrusted.com, or loosely scoped to a parent domain e.g. nottrusted.com. In the latter case, any subdomain of nottrusted.com can access the cookie.',
        severity: 'Medium',
      },
      {
        title: "Server leaks version info via 'Server' HTTP header",
        description:
          'The server is leaking version information via the “Server” HTTP response header. Access to such information may facilitate attackers identifying other vulnerabilities your web/application server is subject to.',
        severity: 'Medium',
      },
      {
        title: 'App crash may disclose error info',
        description:
          'This page contains an error message that may disclose sensitive information like the location of the file that produced the unhandled exception. This information can be used to launch further attacks against the web application.',
        severity: 'Low',
      },
    ],
    Nuclei: [
      {
        name: 'ClockWatch Enterprise - Remote Code Execution',
        description: 'ClockWatch Enterprise is susceptible to remote code execution.',
        tags: 'clockwatch, rce, network',
        severity: 'Critical',
      },
      {
        name: 'ZTE Router Panel - Detect',
        description:
          'Multiple ZTE router panels were detected. These routers have a telnet-hardcoded backdoor account that spawns root shell.',
        tags: 'edb, network, zte, telnet, backdoor, router',
        severity: 'Critical',
      },
      {
        name: 'Ruijie EG Easy Gateway - Remote Command Execution',
        description:
          'Ruijie EG Easy Gateway login.php has remote commmand execution vulnerability, which can lead to the disclosure of administrator account and password.',
        tags: 'ruijie, rce',
        severity: 'Critical',
      },
      {
        name: 'QVISDVR JSF Deserialization - Remote Code Execution',
        description:
          'QVISDVR Java-Deserialization was discovered, which could allow remote code execution.',
        tags: 'qvisdvr, rce, deserialization, jsf, iot',
        severity: 'Critical',
      },
      {
        name: 'elFinder 2.1.58 - Remote Code Execution',
        description:
          'elFinder 2.1.58 is vulnerable to remote code execution. This can allow an attacker to execute arbitrary code and commands on the server hosting the elFinder PHP connector, even with minimal configuration.',
        tags: 'tech, elfinder, oss',
        severity: 'Critical',
      },
      {
        name: 'Header - Remote Command Injection',
        description: 'Headers were tested for remote command injection vulnerabilities.',
        tags: 'fuzz, rce',
        severity: 'Critical',
      },
      {
        name: 'Plastic Admin Console - Authentication Bypass',
        description: 'A Plastic Admin console was discovered.',
        tags: 'plastic, misconfig, intrusive',
        severity: 'Critical',
      },
      {
        name: 'Jupyter Lab - Unauthenticated Access',
        description: 'JupyterLab was able to be accessed without authentication.',
        tags: 'unauth, jupyter, jupyterlab, misconfig',
        severity: 'Critical',
      },
      {
        name: 'Jupyter ipython - Authorization Bypass',
        description: 'Jupyter was able to be accessed without authentication.',
        tags: 'unauth, jupyter, misconfig',
        severity: 'Critical',
      },
      {
        name: 'Redis Sandbox Escape - Remote Code Execution',
        description:
          'This template exploits CVE-2022-0543, a Lua-based Redis sandbox escape. The vulnerability was introduced by Debian and Ubuntu Redis packages that insufficiently sanitized the Lua environment. The maintainers failed to disable the package interface, allowing attackers to load arbitrary libraries.',
        tags: 'cve, cve2022, network, redis, unauth, rce, kev',
        severity: 'Critical',
      },
      {
        name: 'ProFTPd - Remote Code Execution',
        description:
          'ProFTPD 1.3.5 contains a remote code execution vulnerability via the mod_copy module which allows remote attackers to read and write to arbitrary files via the site cpfr and site cpto commands.',
        tags: 'cve, cve2015, ftp, rce, network, proftpd, edb',
        severity: 'Critical',
      },
    ],
  }

  const normalizedQuery = searchTerm.trim().toLowerCase()
  const filteredZap = data.ZAP.filter((item) => {
    if (!normalizedQuery) return true
    const haystack = `${item.title} ${item.description} ${item.severity}`.toLowerCase()
    return haystack.includes(normalizedQuery)
  })
  const filteredNuclei = data.Nuclei.filter((item) => {
    if (!normalizedQuery) return true
    const haystack = `${item.name} ${item.description} ${item.tags} ${item.severity}`.toLowerCase()
    return haystack.includes(normalizedQuery)
  })

  const popover = (
    <Popover id='popover-basic'>
      <Popover.Body>
        Zap will probe the software you have built for typical defects such as missing headers,
        cookie configuration,accidentally deployed files,etc.
      </Popover.Body>
    </Popover>
  )

  const popover2 = (
    <Popover id='popover-basic'>
      <Popover.Body>
        Nuclei will probe your web-availabe services for known defects. It will give you an
        attacker's point of view of your hosted infrastructure, such as your Gitlab server, your
        Wordpress website, your hosted Confluence server, etc.
      </Popover.Body>
    </Popover>
  )
  return (
    <>
      <Helmet>
        <title>SecureSlate | Domains</title>
      </Helmet>
      <DomainPageTitle
        icon='Icon-3.svg'
        title='Domains'
        // desc={totalVendor.toString() + ' Vendors'}
        desc={'1 connected domain'}
      >
        Domains
      </DomainPageTitle>

      <div className='card card-custom '>
        <div className='card-header border-0 pt-6 h-100'>
          <div className='card-title'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          <div className='card-toolbar'>
            {/* begin::Group actions */}
            {/* <CloudListToolbar /> */}
            {/* end::Group actions */}
            <ButtonGroup aria-label='Basic example' className='border'>
              <Button
                variant={activeButton === 'ZAP' ? 'outline-secondary' : 'outline-secondary'}
                onClick={() => setActiveButton('ZAP')}
                style={{
                  backgroundColor: activeButton === 'ZAP' ? '#0e9f6e' : 'transparent',
                  color: activeButton === 'ZAP' ? '#ffffff' : '#5A5A5A',
                  borderColor: activeButton === 'ZAP' ? '#0e9f6e' : '#E4E6EF'
                }}
              >
                <OverlayTrigger trigger={['hover', 'focus']} placement='bottom' overlay={popover}>
                  <i className={`bi bi-exclamation-circle ${activeButton === 'ZAP' ? 'text-white' : ''}`}></i>
                </OverlayTrigger>{' '}
                ZAP
              </Button>
              <Button
                variant={activeButton === 'Nuclei' ? 'outline-secondary' : 'outline-secondary'}
                onClick={() => setActiveButton('Nuclei')}
                style={{
                  backgroundColor: activeButton === 'Nuclei' ? '#0e9f6e' : 'transparent',
                  color: activeButton === 'Nuclei' ? '#ffffff' : '#5A5A5A',
                  borderColor: activeButton === 'Nuclei' ? '#0e9f6e' : '#E4E6EF'
                }}
              >
                <OverlayTrigger trigger={['hover', 'focus']} placement='bottom' overlay={popover2}>
                  <i className={`bi bi-exclamation-circle ${activeButton === 'Nuclei' ? 'text-white' : ''}`}></i>
                </OverlayTrigger>{' '}
                Nuclei
              </Button>
            </ButtonGroup>
          </div>
        </div>

        <div className='card-body py-3 '>
          <div className='table-responsive'>
            {activeButton === 'ZAP' ? (
              <>
                <table className='table align-middle gs-0 gy-4'>
                  <thead>
                  <tr className='fw-bold fs-7 border-bottom border-dotted' style={{color: '#5A5A5A'}}>
                      <th className='ps-4 min-w-400px rounded-start'>TITLE</th>
                      <th className='min-w-500px'>DESCRIPTION</th>
                      <th className='min-w-100px'>SCORE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredZap.length === 0 ? (
                      <tr>
                        <td colSpan={3} className='text-center'>No results</td>
                      </tr>
                    ) : (
                      filteredZap.map((item, idx) => (
                        <tr className=' border-bottom border-dotted' key={`zap-${idx}`}>
                          <td>
                            <div className='d-flex align-items-center mx-5'>
                              {item.title}
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center'>
                              {item.description}
                            </div>
                          </td>
                          <td>
                            <div className={`d-flex align-items-center ${item.severity === 'Critical' ? 'text-danger' : item.severity === 'High' ? 'text-warning' : item.severity === 'Medium' ? 'text-info' : 'text-primary'}`}>
                              {item.severity === 'Critical' && (
                                <i className='bi bi-exclamation-triangle mx-1 text-danger'></i>
                              )}
                              {item.severity === 'High' && (
                                <i className='bi bi-exclamation-circle mx-1 text-warning'></i>
                              )}
                              {item.severity}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </>
            ) : (
              <>
                <table className='table align-middle gs-0 gy-4'>
                  <thead>
                  <tr className='fw-bold fs-7 border-bottom border-dotted' style={{color: '#5A5A5A'}}>
                      <th className='ps-4 min-w-300px rounded-start'>NAME</th>
                      <th className='min-w-400px'>DESCRIPTION</th>
                      <th className='min-w-150px'>TAGS</th>
                      <th className='min-w-100px'>SEVERITY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNuclei.length === 0 ? (
                      <tr>
                        <td colSpan={4} className='text-center'>No results</td>
                      </tr>
                    ) : (
                      filteredNuclei.map((item, idx) => (
                        <tr className=' border-bottom border-dotted' key={`nuclei-${idx}`}>
                          <td>
                            <div className='d-flex align-items-center mx-5'>
                              {item.name}
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center'>
                              {item.description}
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center'>
                              {item.tags}
                            </div>
                          </td>
                          <td>
                            <div className={`d-flex align-items-center ${item.severity === 'Critical' ? 'text-danger' : item.severity === 'High' ? 'text-warning' : item.severity === 'Medium' ? 'text-info' : 'text-primary'}`}>
                              {item.severity === 'Critical' && (
                                <i className='bi bi-exclamation-triangle mx-1 text-danger'></i>
                              )}
                              {item.severity === 'High' && (
                                <i className='bi bi-exclamation-circle mx-1 text-warning'></i>
                              )}
                              {item.severity}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export {DomainChecks}
